import KeyValuePair from '@/modules/core/support/KeyValuePair';
import Service from '@/modules/reservation/model/Service';
import moment from 'moment'

/**
 * Tipo de departamento
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export default class DepartmentType {
    public createdAt = new Date()
    public updateAt = new Date()

    /**
     * Hora de apertura en formato 24 horas, por ejemplo "09:00"
     */
    public openingTime = ''

    /**
     * Hora de cierre en formato 24 horas, por ejemplo "21:00"
     */
    public closingTime = ''

    /**
     * Duración en segundos de la reserva
     */
    public duration = 0

    /**
     * Id de unigest que tiene el tipo de departamento.
     */
    public unigestId = ''

    /**
     * Número de reservas que se permite al usuario realizar
     * al día.
     */
    public reservationsPerDay = 0

    /**
     * Valor en verdadero o falso que indica si las reservas
     * deben realizarse correlativas o no.
     */
    public consecutiveReservations = false

    /**
     * Servicios disponibles para el tipo de departamento
     */
    public availableServices: Service[] = []

    /**
     * Número de serie de las facturas de Unigest generadas cuando se reservas un departamento
     * de este tipo
     */
    public serie = ''

    public constructor (public id = 0, public name = '') {}

    public static deserialize (data: KeyValuePair) {
        const type = new this(data.id, data.name)
        type.createdAt = new Date(data.created_at)
        type.updateAt  = new Date(data.updated_at)
        type.openingTime = data.opening_date
        type.closingTime = data.closing_date
        type.duration = data.duration
        type.unigestId = data.unigest_id || ''
        type.reservationsPerDay = data.reservations_per_day
        type.consecutiveReservations = data.consecutive_reservations
        type.serie = data.serie || ''
        if (Array.isArray(data.available_services)) {
            type.availableServices = data.available_services.map(s => Service.deserialize(s))
        }

        return type
    }

    /**
     * Indica si los departamentos de este tipo cierran el día siguiente al día que
     * abre. Por ejemplo, si el departamento cierra a las 9:00 y cierra a las 1:00
     * del día siguiente
     */
    public get closesNextDay (): boolean {
        if (!this.openingTime || !this.closingTime) {
            return false
        }

        const t = (format: string) => {
            const [h, m] = format.split(':')
            return {
                hour: parseInt(h, 10),
                minutes: parseInt(m, 10),
            }
        }

        const opening = t(this.openingTime)
        const closing = t(this.closingTime)

        return (closing.hour === opening.hour)
            // Si las horas de apertura y cierre son iguales se comprueban los minutos
            ? closing.minutes < opening.minutes
            : closing.hour < opening.hour
    }

    /**
     * Indica si el tipo de departamento es nuevo y todavía no ha sido insertado en el servidor
     */
    public get isNew (): boolean {
        return !this.id
    }

    public serialize (): KeyValuePair {
        return {
            id: this.id,
            name: this.name,
            opening_date: this.openingTime,
            closing_date: this.closingTime,
            duration: this.duration,
            available_services: this.availableServices.map(s => s.id),
            unigest_id: this.unigestId,
            reservations_per_day: this.reservationsPerDay,
            consecutive_reservations: this.consecutiveReservations,
            serie: this.serie,
        }
    }
}
